// src/components/TwoFactorSetup.jsx
// Setting up two-factor authentication with an authenticator app, used by
// the required setup screen after sign-in and by My profile:
//   verify email (if needed) → confirm it's you (if Firebase asks) →
//   scan the QR code → enter the 6-digit code → save recovery codes.
// Also exports the pieces My profile reuses: ReauthBox (confirm it's you)
// and RecoveryCodesPanel (show a new set of codes once).
import { useState } from 'react';
import QRCode from 'qrcode';
import { useAuth, signInMethods } from '../hooks/useAuth';
import { createRecoveryCodes } from '../utils/api';
import { describeAuthError } from '../utils/authErrors';
import { timeZone, tzLabel } from '../utils/timeZone';
import Emo, { emoLabel } from './Emo';

const NOT_ENABLED_MSG = "Two-factor sign-in isn't switched on for the portal yet. Please contact Fax Portal support.";

export function friendlyTwoFactorError(e) {
  if (e.code === 'auth/operation-not-allowed' || e.code === 'auth/admin-restricted-operation') return NOT_ENABLED_MSG;
  if (e.code === 'auth/invalid-verification-code' || e.code === 'auth/missing-code') return "That code didn't work. Enter the current 6-digit code from the app.";
  if (e.code === 'auth/wrong-password' || e.code === 'auth/invalid-credential') return 'That password is not correct.';
  if (e.code === 'auth/unverified-email') return 'Please verify your email address first.';
  if (e.code && e.code.startsWith('auth/')) return describeAuthError(e, 'reauth').message;
  return e.message || 'Something went wrong. Please try again.';
}

const digits6 = v => v.replace(/\D/g, '').slice(0, 6);

// ── Setup flow ───────────────────────────────────────────────
// onEnrolling: called just before the authenticator is saved, so a parent
// can keep this screen open for the recovery codes step.
// onDone: finished (codes saved or skipped). onCancel: optional.
export default function TwoFactorSetup({ onEnrolling, onDone, onCancel, startLabel = 'Start setup' }) {
  const {
    user, userVersion, startTotpEnrollment, finishTotpEnrollment,
    sendVerificationEmail, refreshUser,
  } = useAuth();

  // step: 'start' | 'verify-email' | 'reauth' | 'scan' | 'codes'
  const [step, setStep]     = useState('start');
  const [enroll, setEnroll] = useState(null);   // { secret, key, qrUrl, qrImage }
  const [code, setCode]     = useState('');
  const [codes, setCodes]   = useState(null);
  const [busy, setBusy]     = useState(false);
  const [err, setErr]       = useState('');
  const [verifySent, setVerifySent] = useState(false);
  void userVersion;   // re-render after the account is re-read

  async function begin() {
    setErr('');
    if (!user.emailVerified) { setStep('verify-email'); return; }
    setBusy(true);
    try {
      const data    = await startTotpEnrollment();
      const qrImage = await QRCode.toDataURL(data.qrUrl, { margin: 1, width: 200 });
      setEnroll({ ...data, qrImage });
      setCode('');
      setStep('scan');
    } catch (e) {
      if (e.code === 'auth/requires-recent-login') setStep('reauth');
      else setErr(friendlyTwoFactorError(e));
    }
    setBusy(false);
  }

  async function confirm(e) {
    e.preventDefault();
    setErr(''); setBusy(true);
    try {
      onEnrolling?.();
      await finishTotpEnrollment(enroll.secret, code);
      await makeCodes();
    } catch (e2) {
      setErr(friendlyTwoFactorError(e2));
    }
    setBusy(false);
  }

  async function makeCodes() {
    setErr('');
    try {
      const r = await createRecoveryCodes(() => user.getIdToken(true));
      setCodes(r.codes);
    } catch (e) {
      setCodes(null);
      setErr(`Your authenticator is set up, but we couldn't make your recovery codes: ${e.message}`);
    }
    setStep('codes');
  }

  async function sendVerify() {
    setErr(''); setBusy(true);
    try {
      const r = await sendVerificationEmail();
      if (r.alreadyVerified) { await refreshUser(); setBusy(false); await begin(); return; }
      setVerifySent(true);
    } catch (e) { setErr(friendlyTwoFactorError(e)); }
    setBusy(false);
  }

  async function checkVerified() {
    setErr(''); setBusy(true);
    try {
      await refreshUser();
      if (user.emailVerified) { setBusy(false); await begin(); return; }
      setErr("Your email isn't verified yet. Open the link in the email, then try again.");
    } catch (e) { setErr(friendlyTwoFactorError(e)); }
    setBusy(false);
  }

  const cancelBtn = onCancel && <button type="button" onClick={onCancel} style={S.linkBtn}>Cancel</button>;

  return (
    <div>
      {step === 'start' && (
        <div style={S.row}>
          <button onClick={begin} disabled={busy} className="tfa-primary" style={S.primaryBtn}>{busy ? 'Starting…' : startLabel}</button>
          {cancelBtn}
        </div>
      )}

      {step === 'verify-email' && (
        <div style={S.box}>
          <p style={S.boxText}><strong>First, verify your email address</strong> ({user.email}). We'll email you a link. Open it, then come back here.</p>
          <div style={S.row}>
            <button onClick={sendVerify} disabled={busy || verifySent} className="tfa-primary" style={S.primaryBtn}>
              {verifySent ? 'Email sent' : 'Send verification email'}
            </button>
            <button onClick={checkVerified} disabled={busy} className="tfa-secondary" style={S.secondaryBtn}>I've verified it</button>
            {cancelBtn}
          </div>
        </div>
      )}

      {step === 'reauth' && (
        <ReauthBox
          onConfirmed={() => { setStep('start'); begin(); }}
          onCancel={onCancel ? () => setStep('start') : null} />
      )}

      {step === 'scan' && enroll && (
        <form onSubmit={confirm} style={S.box}>
          <ol style={S.steps}>
            <li>On your phone, open <strong>Google Authenticator</strong> or <strong>Microsoft Authenticator</strong>. If you don't have one, install it free from the App Store or Google Play.</li>
            <li>In the app, tap <strong>+</strong> (add an account) and <strong>scan this QR code</strong>. If you can't scan it, choose to enter a setup key, type the key below and pick <strong>Time-based</strong>.</li>
            <li>Type the <strong>6-digit code</strong> the app now shows for Crazy Pixels Fax Portal.</li>
          </ol>
          <div style={S.scanRow}>
            <img src={enroll.qrImage} alt="QR code to add the Fax Portal to your authenticator app" style={S.qr} />
            <div style={{ minWidth: 0 }}>
              <div style={S.dt}>Setup key</div>
              <code style={S.key}>{enroll.key.replace(/(.{4})/g, '$1 ').trim()}</code>
            </div>
          </div>
          <label style={S.label}>6-digit code
            <input value={code} onChange={e => setCode(digits6(e.target.value))} autoFocus
              inputMode="numeric" autoComplete="one-time-code" placeholder="123456" required
              style={{ ...S.input, ...S.codeInput }} />
          </label>
          <div style={S.row}>
            <button type="submit" disabled={busy || code.length !== 6} className="tfa-primary" style={S.primaryBtn}>{busy ? 'Checking…' : 'Turn on'}</button>
            {cancelBtn}
          </div>
        </form>
      )}

      {step === 'codes' && (
        codes
          ? <RecoveryCodesPanel codes={codes} doneLabel="Continue" onDone={onDone} intro="Two-factor authentication is on." />
          : (
            <div style={S.box}>
              <p style={S.boxText}>You can make recovery codes later in Settings, under Two-factor authentication.</p>
              <div style={S.row}>
                <button onClick={makeCodes} disabled={busy} className="tfa-primary" style={S.primaryBtn}>Try again</button>
                <button onClick={onDone} className="tfa-secondary" style={S.secondaryBtn}>Skip for now</button>
              </div>
            </div>
          )
      )}

      {err && <div style={{ ...S.error, marginTop: 10 }} role="alert">{err}</div>}
    </div>
  );
}

// ── Confirm it's you ─────────────────────────────────────────
// Password accounts type their password; Google accounts use the Google
// pop-up. If 2FA is on, Firebase asks for a code too.
export function ReauthBox({ onConfirmed, onCancel, text = "For your security, please confirm it's you." }) {
  const { user, reauthenticate, resolveReauthWithCode } = useAuth();
  const methods = signInMethods(user);
  // Password when the account has one (Google stays an option); Google-only otherwise
  const [useGoogle, setUseGoogle] = useState(!methods.password);
  const [password, setPassword] = useState('');
  const [code, setCode]         = useState('');
  const [resolver, setResolver] = useState(null);
  const [busy, setBusy]         = useState(false);
  const [err, setErr]           = useState('');

  async function confirmFirst(e) {
    e.preventDefault();
    setErr(''); setBusy(true);
    try {
      const r = await reauthenticate(useGoogle ? null : password);
      if (r.mfa) { setResolver(r.mfa); setBusy(false); return; }
      setBusy(false);
      onConfirmed();
    } catch (e2) { setErr(friendlyTwoFactorError(e2)); setBusy(false); }
  }

  async function confirmCode(e) {
    e.preventDefault();
    setErr(''); setBusy(true);
    try {
      await resolveReauthWithCode(resolver, code);
      setBusy(false);
      onConfirmed();
    } catch (e2) { setErr(friendlyTwoFactorError(e2)); setBusy(false); }
  }

  const cancelBtn = onCancel && <button type="button" onClick={onCancel} style={S.linkBtn}>Cancel</button>;

  if (resolver) {
    return (
      <form onSubmit={confirmCode} style={S.box}>
        <label style={S.label}>Code from your authenticator app
          <input value={code} onChange={e => setCode(digits6(e.target.value))} autoFocus
            inputMode="numeric" autoComplete="one-time-code" placeholder="123456" required
            style={{ ...S.input, ...S.codeInput }} />
        </label>
        {err && <div style={S.error} role="alert">{err}</div>}
        <div style={S.row}>
          <button type="submit" disabled={busy || code.length !== 6} className="tfa-primary" style={S.primaryBtn}>Confirm</button>
          {cancelBtn}
        </div>
      </form>
    );
  }
  return (
    <form onSubmit={confirmFirst} style={S.box}>
      <p style={S.boxText}>{text}</p>
      {!useGoogle && (
        <label style={S.label}>Your password
          <input type="password" value={password} onChange={e => setPassword(e.target.value)} autoFocus
            required autoComplete="current-password" style={S.input} />
        </label>
      )}
      {err && <div style={S.error} role="alert">{err}</div>}
      <div style={S.row}>
        <button type="submit" disabled={busy} className="tfa-primary" style={S.primaryBtn}>{useGoogle ? 'Confirm with Google' : 'Confirm'}</button>
        {methods.password && methods.google && (
          <button type="button" onClick={() => { setUseGoogle(g => !g); setErr(''); }} style={S.linkBtn}>
            {useGoogle ? 'Use my password instead' : 'Use Google instead'}
          </button>
        )}
        {cancelBtn}
      </div>
    </form>
  );
}

// ── Recovery codes, shown once ───────────────────────────────
export function RecoveryCodesPanel({ codes, onDone, doneLabel = 'Done', intro }) {
  const [saved, setSaved]   = useState(false);
  const [copied, setCopied] = useState(false);
  const text = [
    'Crazy Pixels Fax Portal: two-factor recovery codes',
    `Made ${new Date().toLocaleString('en-US', { timeZone: timeZone() })} ${tzLabel()}`,
    'Each code works once. Use one if you lose access to your authenticator app.',
    '',
    ...codes,
  ].join('\n');

  async function copy() {
    try { await navigator.clipboard.writeText(text); setCopied(true); } catch { setCopied(false); }
  }
  function download() {
    const url = URL.createObjectURL(new Blob([text + '\n'], { type: 'text/plain' }));
    const a   = document.createElement('a');
    a.href = url; a.download = 'crazy-pixels-fax-recovery-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div style={S.box}>
      <p style={S.boxText}>
        {intro && <><strong>{intro}</strong> </>}
        <strong>Save your recovery codes.</strong> If you lose your phone, each code lets you sign in once and set up a new authenticator. Keep them somewhere safe, such as a password manager or a printed copy. They won't be shown again.
      </p>
      <ul style={S.codeGrid} aria-label="Recovery codes">
        {codes.map(c => <li key={c} style={S.codeItem}>{c}</li>)}
      </ul>
      <div style={S.row}>
        <button type="button" onClick={download} className="tfa-secondary" style={S.secondaryBtn}><Emo e="⬇" /> Download</button>
        <button type="button" onClick={copy} className="tfa-secondary" style={S.secondaryBtn}>{copied ? emoLabel('✓ Copied') : '⧉ Copy'}</button>
      </div>
      <label style={S.check}>
        <input type="checkbox" checked={saved} onChange={e => setSaved(e.target.checked)} />
        <span>I've saved my recovery codes somewhere safe</span>
      </label>
      <div style={S.row}>
        <button type="button" onClick={onDone} disabled={!saved} className="tfa-primary" style={saved ? S.primaryBtn : { ...S.primaryBtn, ...S.btnOff }}>{doneLabel}</button>
      </div>
    </div>
  );
}

export const S = {
  row: { display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' },
  box: { display: 'flex', flexDirection: 'column', gap: 12, padding: 14, border: '1.5px solid var(--c-e0f2f1)', borderRadius: 10, background: 'var(--c-fbfefd)' },
  boxText: { margin: 0, fontSize: 14, color: 'var(--t-37474f)', lineHeight: 1.55 },
  steps: { margin: 0, paddingLeft: 20, fontSize: 14, color: 'var(--t-37474f)', lineHeight: 1.65, display: 'flex', flexDirection: 'column', gap: 6 },
  scanRow: { display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center' },
  qr: { width: 170, height: 170, border: '1px solid var(--c-eceff1)', borderRadius: 8 },
  dt: { fontSize: 11, fontWeight: 700, color: 'var(--t-607d8b)', textTransform: 'uppercase', letterSpacing: '0.5px' },
  key: { display: 'block', marginTop: 4, fontSize: 14, fontFamily: 'monospace', color: 'var(--t-263238)', overflowWrap: 'anywhere', letterSpacing: '1px' },
  label: { display: 'flex', flexDirection: 'column', gap: 5, fontSize: 13, fontWeight: 600, color: 'var(--t-546e7a)' },
  input: { padding: '10px 12px', borderRadius: 8, border: '1.5px solid var(--c-e0e0e0)', fontSize: 14, color: 'var(--t-263238)', fontFamily: 'inherit' },
  codeInput: { fontSize: 20, letterSpacing: '6px', fontFamily: 'monospace', maxWidth: 180 },
  error: { background: 'var(--c-fce4ec)', color: 'var(--t-b71c1c)', padding: '9px 12px', borderRadius: 8, fontSize: 13, lineHeight: 1.5 },
  primaryBtn: {
    padding: '10px 18px', background: 'var(--c-00897b)', color: 'var(--t-ffffff)', border: 'none',
    borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600, fontFamily: 'inherit'
  },
  secondaryBtn: {
    padding: '10px 16px', background: 'var(--c-ffffff)', color: 'var(--t-37474f)', border: '1.5px solid var(--c-cfd8dc)',
    borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600, fontFamily: 'inherit'
  },
  linkBtn: {
    padding: '10px 6px', background: 'none', border: 'none', color: 'var(--t-546e7a)', cursor: 'pointer',
    fontSize: 14, fontFamily: 'inherit', textDecoration: 'underline'
  },
  btnOff: { opacity: 0.45, cursor: 'default' },
  codeGrid: {
    listStyle: 'none', margin: 0, padding: '12px 14px', display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '8px 16px',
    background: 'var(--c-ffffff)', border: '1px dashed var(--c-b0bec5)', borderRadius: 8
  },
  codeItem: { fontFamily: 'monospace', fontSize: 15, color: 'var(--t-263238)', letterSpacing: '1px' },
  check: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: 'var(--t-37474f)', cursor: 'pointer' },
};
